/**
 * The three-column ledger — `design_handoff_order_and_lottery` §"Screens".
 *
 * ONE TABLE, SIX STATES, AND THE OUTER COLUMNS FILL FIVE DAYS APART. That is the whole design
 * argument: since 2020-II-1 the lottery hands out the ORDER GMs choose in, not the slots, so the
 * page has to hold two answers that arrive on different evenings without pretending either is the
 * other. A ledger whose left column fills on 26 August and whose right column fills on 31 August
 * says that without a paragraph.
 *
 * TWO RULES THE BUILD GOT WRONG BEFORE, both stated in the handoff because they were expensive:
 *
 *   1. IDENTITY IS NOT AN OUTCOME — BUT WHICH ORDER THE ROWS ARE IN DECIDES WHETHER IT IS. Before
 *      the draw the ledger maps the lottery BASIS, so a name in row 7 carries no information and
 *      all ten belong on screen. Mid-draw it maps the DRAWN order, so an undrawn row's name IS the
 *      result, and printing it is showing the room the answer early. Same column, opposite
 *      meanings, and the only thing separating them is the phase.
 *
 *   2. EVERY PHASE-VARYING VALUE NEEDS ONE ENTRY PER PHASE. Two-way flags — `drawn`, `slotted` —
 *      fall through in whichever of the six states they were not written for, and what the reader
 *      gets is a column header contradicting the sub-line beside it. So the heads are a table with
 *      six rows and no `else`.
 *
 * WHY IT IS A MODULE. Three screens want pieces of this — the page, the reveal's own ledger and
 * the commissioner's launcher — and every figure on it is derived from the same four documents.
 * A percentage computed in a render is a percentage nobody can test; so is a column header.
 */

import { minimumPreferences } from './draftOrder.js';
import type { RankingCensus } from './rankingCensus.js';
import { ordinal } from './lotteryReveal.js';
import type { SwapState } from './thirdPlaceSwap.js';

export type OrderPhase = 'pre' | 'launch' | 'draw' | 'after' | 'held' | 'published';

/* -- which of the six ------------------------------------------------------------------------- */

export interface PhaseInput {
  /** `draftState.lotteryDrawnAt` — the broadcast. */
  drawnAt?: string | null;
  /** Names landed so far. Equal to `teams` once the reveal has finished playing. */
  drawn: number;
  teams: number;
  /** The commissioner is standing in front of the launcher. */
  launching?: boolean;
  /** He is walking a rehearsal, which writes nothing. */
  rehearsing?: boolean;
  /** The 5pm resolve has run. */
  resolvedAt?: string | null;
  /** The commissioner has released it. */
  publishedAt?: string | null;
}

/**
 * WHICH STATE THE PAGE IS IN, in one place.
 *
 * Ordered from the outside in: publication is the end of the road and beats everything, and
 * rehearsal is a lens over the launcher rather than a state of the season — a rehearsal after
 * publication is still a rehearsal, and it must not put the page back into `draw`.
 */
export function orderPhase(i: PhaseInput): OrderPhase {
  if (i.rehearsing) return 'draw';
  if (i.publishedAt) return 'published';
  if (i.resolvedAt) return 'held';
  if (i.drawnAt && i.drawn >= i.teams) return 'after';
  if (i.drawnAt) return 'draw';
  return i.launching ? 'launch' : 'pre';
}

/* -- the rows ---------------------------------------------------------------------------------- */

export interface LedgerRow {
  /** 1-based choosing position. */
  position: number;
  /**
   * Whose row it is, or null when the name would BE the result — see rule 1 above. A null name
   * renders as a dashed circle and a placeholder bar, never as blank space.
   */
  gm: string | null;
  /** Has this position come out of the machine (or been settled by the standings)? */
  drawn: boolean;
  /** The first-round slot, once there is one to show. */
  slot: number | null;
  /** Resolved and not yet released: a slot exists and is nobody's to see. */
  sealed: boolean;
  /**
   * How he came by the slot — `1st choice`, `2nd choice`, `no ranking`.
   *
   * THE PREFERENCE RANK ONLY. Slot ordinals and choice ordinals are two number systems, and
   * "4th · 2nd — 2nd gone" is unreadable. What was taken before him is not his business anyway.
   */
  slotNote: string;
  /** `placed by rule`, `may take Matt's`, `Ari may take this`, `Pending`, `Lapsed`. */
  tag: string | null;
  tagTone: 'neutral' | 'amber' | null;
  mine: boolean;
  /**
   * WHETHER THE LEAGUE HAS HIS RANKING — a receipt, not a ranking.
   *
   * THREE STATES, NOT TWO. `draft` is the one that was missing and it is the one that misleads:
   * `resolveDraftSlots` reads `p.ranked` and never looks at `submittedAt`, so a list that was
   * autosaved and never sent IS used when the order resolves. Showing that man an empty ring told
   * nine people he had ranked nothing while the resolve was about to honour his list.
   *
   * Null on a row that cannot honestly say any of it — see `submittedMark`.
   */
  submitted: 'in' | 'draft' | 'out' | null;
  /** The stamp behind an `in`, so the mark can say WHEN rather than only yes. */
  submittedAt: string | null;
}

export interface LedgerInput {
  phase: OrderPhase;
  /** `lotteryBasis(...)` — the order the odds are keyed to. What `pre` and `launch` map. */
  basis: readonly string[];
  /** The choosing order the draw produced. What every other phase maps. */
  order: readonly string[];
  /** Names landed. Only consulted in `draw`. */
  drawn: number;
  teams: number;
  me: string;
  /** GM → first-round slot, once resolved. */
  slots?: Readonly<Record<string, number>>;
  /** GM → his submitted ranking, for the slot note. */
  prefs?: Readonly<Record<string, readonly number[]>>;
  /**
   * GM → when his ranking was recorded. A GM missing from the map, or carrying a null, has not
   * sent one. Omit the map entirely on a screen that is not tracking submission at all — every
   * row then reports null rather than accusing ten GMs of missing a deadline.
   */
  submitted?: Readonly<Record<string, string | null>>;
  /**
   * The league's ranking census, for the third state.
   *
   * Only `savedBy` is read here — who has a list the resolve will use but has not sent it. Kept
   * separate from `submitted` so a screen can carry the stamps without having to build a census,
   * and so the two can never disagree about who SENT one.
   */
  rankings?: Pick<RankingCensus, 'savedBy'>;
  /** Who was lifted to position 2 by 2020-II-1 §3, if anybody. */
  placedByRule?: string | null;
  /** The third-place option, on the two rows it touches. */
  option?: { third: string; fourth: string; state: SwapState } | null;
}

const up = (s: string | null | undefined) => (s ?? '').toUpperCase();

/**
 * The ledger, one row per choosing position.
 *
 * `basis` in the two states before the draw and `order` in the four after it — which is the whole
 * of rule 1, written once so no screen has to remember it.
 */
export function ledgerRows(i: LedgerInput): LedgerRow[] {
  const beforeDraw = i.phase === 'pre' || i.phase === 'launch';
  const source = beforeDraw ? i.basis : i.order;
  const me = up(i.me);
  const slots = i.slots ?? {};
  const prefs = i.prefs ?? {};

  return Array.from({ length: i.teams }, (_, idx) => {
    const position = idx + 1;
    /* Filling from the bottom: after `drawn` names the last `drawn` positions are the ones out. */
    const drawn = beforeDraw ? false
      : i.phase === 'draw' ? position > i.teams - i.drawn
        : true;
    /*
     * The one line rule 1 turns on. Before the draw every name is safe because the row means
     * "this is where the odds put you"; during it an undrawn row's name is the answer.
     */
    const named = beforeDraw || drawn;
    const gm = named ? (source[idx] ?? null) : null;
    const slot = gm && slots[up(gm)] !== undefined ? slots[up(gm)]! : null;

    return {
      position,
      gm,
      drawn,
      /*
       * SEALED MEANS THE NUMBER IS NOT ON SCREEN. It was carried here for the held state too,
       * which put every GM's slot in the DOM three hours before the league was allowed to see it
       * — visible to anyone who opened the inspector, and precisely the private advantage the
       * cure window exists to prevent (`publication.ts`). Only `published` carries the figure.
       */
      slot: i.phase === 'published' ? slot : null,
      sealed: i.phase === 'held' && slot !== null,
      slotNote: i.phase === 'published' && gm && slot !== null
        ? choiceNote(prefs[up(gm)], slot) : '',
      ...tagFor(gm, i),
      mine: named && up(gm) === me,
      ...submittedMark(i.phase, gm, i.submitted, i.rankings),
    };
  });
}

/**
 * THE RECEIPT ON THE ROW — who has turned a ranking in, before the deadline (Tim, 31 August).
 *
 * A COUNT IS NOT A RECEIPT. The strip has said "League submitted · 2 of 10" since Pass 05, which
 * tells a GM the league is behind and tells him nothing about himself, and tells a commissioner
 * chasing the stragglers nothing at all about WHICH two. The mark is a fact about a named GM,
 * which is what both readers were actually looking for.
 *
 * IT IS NOT THE RANKING. Everything the seal protects — what a GM wants, and therefore what is
 * left for the man choosing after him — stays sealed. That his list exists is not competitive
 * information; the league already prints the count of them.
 *
 * TWO ROWS CANNOT SAY IT, and both silences are deliberate:
 *
 *   AN UNDRAWN ROW mid-draw has no name on it, because the name would be the result. A mark
 *   beside a hole would put one back — "row 7 has submitted" is a fact about a person, and the
 *   row is refusing to say which person that is.
 *
 *   A PUBLISHED BOARD has a slot on every row, and that is the better answer to the same
 *   question. A tick meaning "we had his list in time" competes with the number it produced.
 */
export function submittedMark(
  phase: OrderPhase,
  gm: string | null,
  stamps: Readonly<Record<string, string | null>> | undefined,
  rankings?: Pick<RankingCensus, 'savedBy'>,
): Pick<LedgerRow, 'submitted' | 'submittedAt'> {
  if (!gm || !stamps || phase === 'published') return { submitted: null, submittedAt: null };
  const at = stamps[up(gm)] ?? null;
  if (at) return { submitted: 'in', submittedAt: at };
  /* A list the resolve will read, that he never sent. Not the same thing as ranking nothing. */
  const saved = rankings?.savedBy.some((g) => up(g) === up(gm)) ?? false;
  return { submitted: saved ? 'draft' : 'out', submittedAt: null };
}

/**
 * How he came by it, in the preference's own terms.
 *
 * `no ranking` rather than "took what was left" — §6 is not a failure and the sentence should not
 * read as one. A GM who ranked nothing gets the earliest slot open, which is the rule working.
 */
export function choiceNote(ranked: readonly number[] | undefined, slot: number): string {
  if (!ranked || ranked.length === 0) return 'no ranking';
  const at = ranked.indexOf(slot);
  if (at < 0) return 'best still open';
  return `${at + 1}${ordinal(at + 1)} choice`;
}

/**
 * The chip on a row, and there is at most one.
 *
 * THE OPTION'S TAG MOVES WITH ITS STATE — absent before the man is drawn, amber `Pending` inside
 * the window, neutral `Lapsed` after. A published board still advertising an option that expired
 * on the 31st is telling a GM something untrue about a thing he can no longer do.
 */
function tagFor(gm: string | null, i: LedgerInput): Pick<LedgerRow, 'tag' | 'tagTone'> {
  if (!gm) return { tag: null, tagTone: null };
  const who = up(gm);
  if (i.placedByRule && who === up(i.placedByRule)) {
    return { tag: 'placed by rule', tagTone: 'neutral' };
  }
  const o = i.option;
  if (!o) return { tag: null, tagTone: null };
  if (who === up(o.third)) {
    if (o.state === 'pending') return { tag: 'Pending', tagTone: 'amber' };
    if (o.state === 'taken') return { tag: 'took the swap', tagTone: 'neutral' };
    if (o.state === 'lapsed') return { tag: 'Lapsed', tagTone: 'neutral' };
    return { tag: 'declined', tagTone: 'neutral' };
  }
  /*
   * AND NOTHING ON 4th'S ROW. He is the exposed party and the handoff marks him on the REVEAL —
   * "Ari may take this", beside the result he can lose — not here. On a 390px ledger row the
   * chip pushed his team name into an ellipsis to say something the option card in the aside
   * already says, naming both men, on every one of these screens.
   */
  return { tag: null, tagTone: null };
}

/* -- the column heads -------------------------------------------------------------------------- */

export type HeadTone = 'accent' | 'rehearsal' | 'pass' | 'muted' | 'amber';

export interface Head {
  label: string;
  /** The sub-line under it, which is where the phase actually shows. */
  sub: string;
  tone: HeadTone;
}

export interface HeadInput {
  phase: OrderPhase;
  drawn: number;
  teams: number;
  rehearsing?: boolean;
  /** "26 August" — the day the draw ran. */
  settledOn?: string | null;
  /** "31 August" — the day the order was released. */
  publishedOn?: string | null;
}

/**
 * Three heads, one entry per phase, no fall-through.
 *
 * Written as a lookup rather than a ternary chain for the reason rule 2 gives: the chain is where
 * a missing state silently borrows the neighbouring one's sub-line, and the reader is left with a
 * header that contradicts the column beside it.
 */
export function columnHeads(i: HeadInput): [Head, Head, Head] {
  const order: Head = i.rehearsing
    ? { label: 'Choosing order', sub: 'rehearsal only', tone: 'rehearsal' }
    : {
      label: 'Choosing order',
      sub: ORDER_SUB[i.phase](i),
      tone: i.phase === 'pre' || i.phase === 'launch' ? 'accent' : 'pass',
    };

  const gm: Head = {
    label: 'GM',
    sub: i.phase === 'pre' || i.phase === 'launch' ? 'in lottery-basis order' : 'ten GMs',
    tone: 'muted',
  };

  const slot: Head = {
    label: 'Draft slot',
    sub: SLOT_SUB[i.phase](i),
    tone: i.phase === 'held' ? 'amber' : i.phase === 'published' ? 'pass' : 'muted',
  };

  return [order, gm, slot];
}

const ORDER_SUB: Record<OrderPhase, (i: HeadInput) => string> = {
  pre: () => 'decided tonight',
  launch: () => 'decided tonight',
  draw: (i) => `drawing now — ${i.drawn} of ${i.teams}`,
  after: (i) => settled(i),
  held: (i) => settled(i),
  published: (i) => settled(i),
};

const SLOT_SUB: Record<OrderPhase, (i: HeadInput) => string> = {
  pre: () => 'resolves 31 August at 5pm',
  launch: () => 'resolves 31 August at 5pm',
  draw: () => 'resolves 31 August at 5pm',
  after: () => 'resolves 31 August at 5pm',
  held: () => 'resolved, awaiting release',
  published: (i) => (i.publishedOn ? `published ${i.publishedOn}` : 'published'),
};

const settled = (i: HeadInput) => (i.settledOn ? `settled ${i.settledOn}` : 'settled');

/* -- how many ranks a GM needs ------------------------------------------------------------------ */

export interface RanksLine {
  /** The headline number, and the only thing on the phone at 44px. */
  need: number;
  done: number;
  /** "Four GMs pick before you, so six ranks covers you. You've done four." */
  said: string;
  /** "You choose 5th — six ranks is all you need" */
  title: string;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const word = (n: number) => WORDS[n] ?? String(n);

/**
 * THE ONE NUMBER THAT CHANGES WHAT A GM DOES NEXT, so it is derived from the rulebook and not
 * from the mock.
 *
 * The handoff writes the requirement as `11 − position`; 2020-II-1 §4 writes it as the lottery
 * winner needing two and the runner-up three, which is `position + 1`. The two agree at position
 * 5 — the example the handoff works through — and disagree everywhere else, by as much as eight
 * ranks at the top of the board. `minimumPreferences` has encoded the rulebook's version since
 * Pass 05 and it is the one the resolve actually walks, so it is the one here. Raised with Tim.
 */
export function ranksLine(position: number, teams: number, done: number): RanksLine {
  const need = minimumPreferences(position, teams);
  const before = position - 1;
  const title = `You choose ${position}${ordinal(position)} — ${word(need)} rank`
    + `${need === 1 ? '' : 's'} is all you need`;
  const said = before === 0
    ? `Nobody picks before you, so ${word(need)} ranks covers you.`
    : `${cap(word(before))} GM${before === 1 ? '' : 's'} pick${before === 1 ? 's' : ''} before you, `
      + `so ${word(need)} ranks covers you.`;
  const doneSaid = done >= need ? ` You've done ${word(done)}, so you're covered.`
    : done === 0 ? " You haven't ranked any yet."
      : ` You've done ${word(done)}.`;
  return { need, done, title, said: said + doneSaid };
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* -- what a commissioner is checking, in the held state ----------------------------------------- */

export interface Check {
  state: 'ok' | 'lapsed' | 'note';
  said: string;
}

export interface CheckInput {
  teams: number;
  /** How many GMs submitted a ranking. */
  ranked: number;
  /** Every slot resolved to exactly one GM. */
  slotsComplete: boolean;
  toiletBowlWinner?: string | null;
  /** Where he ended up choosing. */
  toiletBowlPosition?: number | null;
  option?: { third: string; fourth: string; state: SwapState;
    thirdPosition: number; fourthPosition: number } | null;
  /** GMs who ranked nothing, so §6 gave them the earliest slot left. */
  unranked?: readonly string[];
  /** Display names, so the check list reads like the room talks. */
  name?: (gm: string) => string;
}

/**
 * THE CHECK LIST IS ABOUT THE INPUTS, NOT THE MATHS. Its footer says so, and it is the reason the
 * held state exists at all: `resolveDraftSlots` has one right answer and a commissioner cannot
 * improve on it. What he can catch is a ranking that never saved and an option nobody answered.
 */
export function resolutionChecks(i: CheckInput): Check[] {
  const who = i.name ?? ((g: string) => g);
  const out: Check[] = [];

  out.push({
    state: i.ranked >= i.teams && i.slotsComplete ? 'ok' : 'note',
    said: i.ranked >= i.teams
      ? `All ${word(i.teams)} rankings in and every slot resolved cleanly`
      : `${cap(word(i.ranked))} of ${word(i.teams)} ranked; the rest took the best slot left (§6)`,
  });

  if (i.toiletBowlWinner) {
    out.push({
      state: 'ok',
      said: i.toiletBowlPosition === 2
        ? `${who(i.toiletBowlWinner)} took position 2 on the Toilet Bowl right`
        : `${who(i.toiletBowlWinner)} won the draw outright, so the Toilet Bowl right did not apply`,
    });
  }

  const o = i.option;
  if (o) {
    const third = who(o.third);
    const fourth = who(o.fourth);
    const held = `${third} holds ${o.thirdPosition}${ordinal(o.thirdPosition)} `
      + `and ${fourth} ${o.fourthPosition}${ordinal(o.fourthPosition)}, as drawn`;
    if (o.state === 'lapsed') {
      out.push({ state: 'lapsed', said: `${third} never answered by 5pm, so ${held}` });
    } else if (o.state === 'declined') {
      out.push({ state: 'ok', said: `${third} declined the swap, so ${held}` });
    } else if (o.state === 'taken') {
      out.push({
        state: 'ok',
        said: `${third} took ${fourth}'s turn, and both chose from where they ended up`,
      });
    } else {
      out.push({ state: 'note', said: `${third} has not answered yet — it lapses at 5pm` });
    }
  }

  for (const gm of i.unranked ?? []) {
    out.push({ state: 'lapsed', said: `${who(gm)} never ranked anything, so he took the best slot left` });
  }

  return out;
}

/* -- how you got 5th ---------------------------------------------------------------------------- */

export interface TrailLine {
  gm: string;
  said: string;
  mine: boolean;
}

export interface TrailInput {
  /** The choosing order actually walked, after the lift and any swap. */
  order: readonly string[];
  slots: Readonly<Record<string, number>>;
  prefs?: Readonly<Record<string, readonly number[]>>;
  me: string;
  placedByRule?: string | null;
  name?: (gm: string) => string;
}

/**
 * The five lines ending on your own turn.
 *
 * WHAT OTHER LISTS DID, NEVER WHAT THEY WERE. "Amit wanted 1st, took 3rd" is a fact about the
 * board — the slot he ended on and the one he had ranked first, which the board already shows.
 * Whose ranking blocked whom is not the reader's business, and printing another man's list to
 * explain your own slot is the one thing this trail must never do.
 */
export function slotTrail(i: TrailInput): TrailLine[] {
  const who = i.name ?? ((g: string) => g);
  const me = up(i.me);
  const at = i.order.findIndex((g) => up(g) === me);
  if (at < 0) return [];

  return i.order.slice(0, at + 1).map((gm, idx) => {
    const slot = i.slots[up(gm)];
    const mine = idx === at;
    if (slot === undefined) return { gm, said: `${who(gm)} chose`, mine };

    if (mine) {
      const note = choiceNote(i.prefs?.[up(gm)], slot);
      return {
        gm,
        said: `Your turn — ${slot}${ordinal(slot)} open, your ${note === 'no ranking'
          ? 'ranking was empty' : note === 'best still open' ? 'list was used up' : note}`,
        mine,
      };
    }

    if (i.placedByRule && up(gm) === up(i.placedByRule)) {
      return { gm, said: `${who(gm)} took ${slot}${ordinal(slot)} on the Toilet Bowl right`, mine };
    }

    const ranked = i.prefs?.[up(gm)];
    const wanted = ranked?.[0];
    if (wanted !== undefined && wanted !== slot) {
      return { gm, said: `${who(gm)} wanted ${wanted}${ordinal(wanted)}, took ${slot}${ordinal(slot)}`, mine };
    }
    return { gm, said: `${who(gm)} chose ${slot}${ordinal(slot)}`, mine };
  });
}
